package com.arena_web.ui.components.chat

import androidx.compose.runtime.*
import com.arena_web.data.dto.FollowUserDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.widgets.InlineSpinner
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.bodyLMedium
import com.arena_web.ui.theme.bodyLRegular
import com.arena_web.ui.theme.bodyMRegular
import com.arena_web.ui.theme.bodySRegular
import com.arena_web.ui.theme.h4
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.TextInput
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.url.URL
import org.w3c.files.FileReader
import org.w3c.files.get

enum class NewChatTab { DIRECT, GROUP }

/** Gradient border overlay — reusable within this file */
@Composable
private fun GradientBorderOverlay(radiusPx: Int) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("border", "0.3px solid transparent")
                property("border-radius", s(radiusPx))
                property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("-webkit-mask-composite", "xor")
                property("mask-composite", "exclude")
                property("pointer-events", "none")
            }
    )
}

@Composable
fun NewChatDialog(
    following: List<FollowUserDto>,
    isLoading: Boolean,
    isLoadingMore: Boolean,
    hasMore: Boolean,
    searchQuery: String,
    isCreating: Boolean,
    onSearchQueryChanged: (String) -> Unit,
    onCreateDirect: (targetUserId: String) -> Unit,
    onCreateGroup: (name: String, imageBytes: ByteArray?, imageFileName: String?, imageContentType: String?) -> Unit,
    onLoadMore: () -> Unit,
    onDismiss: () -> Unit
) {
    val t = LocalAppStrings.current
    var activeTab by remember { mutableStateOf(NewChatTab.DIRECT) }
    var groupName by remember { mutableStateOf("") }
    var imageBytes by remember { mutableStateOf<ByteArray?>(null) }
    var imageFileName by remember { mutableStateOf<String?>(null) }
    var imageContentType by remember { mutableStateOf<String?>(null) }
    var imagePreviewUrl by remember { mutableStateOf<String?>(null) }

    // Overlay backdrop
    Box(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("position", "fixed")
                property("top", "0")
                property("left", "0")
                property("z-index", "1000")
                property("background", "rgba(0, 0, 0, 0.6)")
            }
            .onClick { onDismiss() },
        contentAlignment = Alignment.Center
    ) {
        // Dialog wrapper with gradient border
        Box(
            modifier = Modifier
                .sWidth(440)
                .styleModifier {
                    property("position", "relative")
                    property("border-radius", s(18))
                }
                .onClick { it.stopPropagation() }
        ) {
            GradientBorderOverlay(18)

            // Dialog content
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("background", "var(--l-s2, #09142d)")
                        property("border-radius", s(18))
                        property("padding", "${s(22)} ${s(24)} ${s(40)} ${s(24)}")
                        property("box-shadow", "0 20px 60px rgba(0,0,0,0.5)")
                        property("gap", s(40))
                    }
            ) {
                // Top section: title + tabs + content
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .styleModifier { property("gap", s(24)) },
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    // Title + Segmented Control
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .styleModifier { property("gap", s(22)) }
                    ) {
                        // Title
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .styleModifier { property("padding", "${s(8)} 0") }
                        ) {
                            SpanText(
                                t.chatNewChatTitle,
                                modifier = Modifier.h4().color(com.arena_web.ui.theme.ThemeVars.Text2)
                            )
                        }

                        // Segmented Control
                        SegmentedControl(
                            activeTab = activeTab,
                            onTabChanged = { activeTab = it }
                        )
                    }

                    // Tab content
                    when (activeTab) {
                        NewChatTab.DIRECT -> DirectTabContent(
                            searchQuery = searchQuery,
                            onSearchChanged = onSearchQueryChanged,
                            results = following,
                            isLoading = isLoading,
                            isLoadingMore = isLoadingMore,
                            hasMore = hasMore,
                            isCreating = isCreating,
                            onCreateDirect = onCreateDirect,
                            onLoadMore = onLoadMore,
                        )

                        NewChatTab.GROUP -> GroupTabContent(
                            groupName = groupName,
                            onGroupNameChanged = { groupName = it },
                            imagePreviewUrl = imagePreviewUrl,
                            onPickImage = {
                                val input = document.createElement("input") as HTMLInputElement
                                input.type = "file"
                                input.accept = "image/*"
                                input.onchange = { _ ->
                                    val file = input.files?.get(0)
                                    if (file != null) {
                                        // Create preview URL
                                        imagePreviewUrl = URL.createObjectURL(file)
                                        imageFileName = file.name
                                        imageContentType = file.type
                                        // Read bytes
                                        val reader = FileReader()
                                        reader.onload = {
                                            val arrayBuffer = reader.result.unsafeCast<org.khronos.webgl.ArrayBuffer>()
                                            val int8Array = org.khronos.webgl.Int8Array(buffer = arrayBuffer)
                                            imageBytes = int8Array.unsafeCast<ByteArray>()
                                        }
                                        reader.readAsArrayBuffer(file)
                                    }
                                }
                                input.click()
                            },
                            isCreating = isCreating,
                            onCreateGroup = { onCreateGroup(groupName.trim(), imageBytes, imageFileName, imageContentType) },
                            onCancel = onDismiss
                        )
                    }
                }
            }
        }
    }
}

// ── Segmented Control ──

@Composable
private fun SegmentedControl(activeTab: NewChatTab, onTabChanged: (NewChatTab) -> Unit) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier.styleModifier {
            property("position", "relative")
            property("border-radius", s(34))
        }
    ) {
        GradientBorderOverlay(34)
        Row(
            modifier = Modifier
                .styleModifier {
                    property("height", s(48))
                    property("padding", s(4))
                    property("border-radius", s(34))
                    property("background", AppGradients.InteractiveSecondary)
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            SegmentTab(t.chatTabDirectMessage, activeTab == NewChatTab.DIRECT) { onTabChanged(NewChatTab.DIRECT) }
            SegmentTab(t.chatTabCommunity, activeTab == NewChatTab.GROUP) { onTabChanged(NewChatTab.GROUP) }
        }
    }
}

@Composable
private fun SegmentTab(label: String, isActive: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .sHeight(40)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("padding", "${s(8)} ${s(26)}")
                property("border-radius", s(28))
                property("white-space", "nowrap")
                if (isActive) {
                    property("background", "#0061ff")
                }
            },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            label,
            modifier = Modifier.bodyLMedium().color(if (isActive) org.jetbrains.compose.web.css.Color(AppColors.PrimaryWhite.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
        )
    }
}

// ── Direct Message Tab ──

@Composable
private fun DirectTabContent(
    searchQuery: String,
    onSearchChanged: (String) -> Unit,
    results: List<FollowUserDto>,
    isLoading: Boolean,
    isLoadingMore: Boolean,
    hasMore: Boolean,
    isCreating: Boolean,
    onCreateDirect: (String) -> Unit,
    onLoadMore: () -> Unit,
) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(24)) }
    ) {
        // Search field with gradient border
        Box(
            modifier = Modifier.fillMaxWidth().styleModifier {
                property("position", "relative")
                property("border-radius", s(28))
            }
        ) {
            GradientBorderOverlay(28)
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("height", s(44))
                        property("border-radius", s(28))
                        property("padding", "${s(0)} ${s(8)} ${s(0)} ${s(12)}")
                        property("gap", s(6))
                        property("background", AppGradients.InteractiveSecondary)
                    },
                verticalAlignment = Alignment.CenterVertically
            ) {
                Image(
                    src = AppIcons.Search,
                    modifier = Modifier.sSize(24).styleModifier {
                        property("opacity", "0.5")
                        property("flex-shrink", "0")
                    }
                )
                TextInput(
                    value = searchQuery,
                    attrs = Modifier
                        .fillMaxWidth()
                        .bodyLRegular()
                        .color(com.arena_web.ui.theme.ThemeVars.Text1)
                        .styleModifier {
                            property("background", "transparent")
                            property("border", "none")
                            property("outline", "none")
                            property("font-family", "Urbanist, sans-serif")
                        }
                        .toAttrs {
                            attr("placeholder", t.chatSearchPeoplePlaceholder)
                            onInput { e -> onSearchChanged(e.target.asDynamic().value as String) }
                        }
                )
            }
        }

        // Friends list. Scroll handler fires `onLoadMore` once the viewer
        // gets within ~80px of the bottom — same threshold the feed sidebar
        // uses — so paging feels seamless and avoids a "load more" button.
        // The list now reflects whatever the server returned for the active
        // search query, so we keep paging even while a query is typed.
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("max-height", s(260))
                    property("overflow-y", "auto")
                    property("scrollbar-width", "none")
                    property("gap", s(24))
                }
                .attrsModifier {
                    onScroll { evt ->
                        if (hasMore && !isLoading && !isLoadingMore) {
                            val el = evt.target.unsafeCast<org.w3c.dom.HTMLElement>()
                            val nearBottom =
                                el.scrollHeight - el.scrollTop - el.clientHeight < 80.0
                            if (nearBottom) onLoadMore()
                        }
                    }
                }
        ) {
            if (isLoading) {
                Box(
                    modifier = Modifier.fillMaxWidth().sPadding(24, 0),
                    contentAlignment = Alignment.Center
                ) {
                    Box {} /* no loading indicator (user pref) */
                }
            } else if (results.isEmpty()) {
                Box(
                    modifier = Modifier.fillMaxWidth().sPadding(24, 0),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        if (searchQuery.isBlank()) t.chatNotFollowingAnyone else t.chatNoResults,
                        modifier = Modifier.bodyMRegular().color(com.arena_web.ui.theme.ThemeVars.Text3)
                    )
                }
            } else {
                results.forEach { user ->
                    FollowingListItem(
                        user = user,
                        isCreating = isCreating,
                        onClick = { onCreateDirect(user.userId) }
                    )
                }
                if (isLoadingMore) {
                    Box(
                        modifier = Modifier.fillMaxWidth().sPadding(8, 0),
                        contentAlignment = Alignment.Center
                    ) {
                        Box {} /* no loading indicator (user pref) */
                    }
                }
            }
        }
    }
}

// ── Community Tab ──

@Composable
private fun GroupTabContent(
    groupName: String,
    onGroupNameChanged: (String) -> Unit,
    imagePreviewUrl: String?,
    onPickImage: () -> Unit,
    isCreating: Boolean,
    onCreateGroup: () -> Unit,
    onCancel: () -> Unit
) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(28)) },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Camera avatar / image preview
        Box(
            modifier = Modifier
                .sSize(80)
                .sBorderRadius(100)
                .cursor(Cursor.Pointer)
                .onClick { onPickImage() }
                .styleModifier {
                    property("background", "rgba(0, 36, 93, 0.8)")
                    property("flex-shrink", "0")
                    property("overflow", "hidden")
                },
            contentAlignment = Alignment.Center
        ) {
            if (imagePreviewUrl != null) {
                Image(
                    src = imagePreviewUrl,
                    modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover)
                )
            } else {
                Image(src = AppIcons.Camera, modifier = Modifier.sSize(24))
            }
        }

        // Community Name field
        Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(6)) }) {
            SpanText(
                t.chatCommunityNameLabel,
                modifier = Modifier
                    .bodyMRegular()
                    .color(Color("#818d9c"))
                    .styleModifier { property("padding-left", s(4)) }
            )
            Box(
                modifier = Modifier.fillMaxWidth().styleModifier {
                    property("position", "relative")
                    property("border-radius", s(28))
                }
            ) {
                GradientBorderOverlay(28)
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .styleModifier {
                            property("height", s(44))
                            property("border-radius", s(28))
                            property("padding", "${s(0)} ${s(8)} ${s(0)} ${s(12)}")
                            property("gap", s(12))
                            property("background", AppGradients.InteractiveSecondary)
                        },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    TextInput(
                        value = groupName,
                        attrs = Modifier
                            .fillMaxWidth()
                            .bodyLRegular()
                            .color(com.arena_web.ui.theme.ThemeVars.Text1)
                            .styleModifier {
                                property("background", "transparent")
                                property("border", "none")
                                property("outline", "none")
                                property("font-family", "Urbanist, sans-serif")
                            }
                            .toAttrs {
                                attr("placeholder", t.chatCommunityNamePlaceholder)
                                onInput { e -> onGroupNameChanged(e.target.asDynamic().value as String) }
                            }
                    )
                }
            }
        }
    }

    // Action buttons
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("gap", s(12))
                property("justify-content", "flex-end")
            }
    ) {
        // Cancel button with gradient border
        Box(
            modifier = Modifier.styleModifier {
                property("position", "relative")
                property("border-radius", s(22))
            }
        ) {
            GradientBorderOverlay(22)
            Box(
                modifier = Modifier
                    .sHeight(40)
                    .cursor(Cursor.Pointer)
                    .onClick { onCancel() }
                    .styleModifier {
                        property("padding", "${s(8)} ${s(26)}")
                        property("border-radius", s(22))
                        property("background", AppGradients.InteractiveSecondary)
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText(t.commonCancel, modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2))
            }
        }

        // Create Community button
        Box(
            modifier = Modifier
                .sHeight(40)
                .cursor(
                    if (groupName.isNotBlank() && !isCreating) Cursor.Pointer else Cursor.Default
                )
                .onClick {
                    if (groupName.isNotBlank() && !isCreating) onCreateGroup()
                }
                .styleModifier {
                    property("padding", "${s(8)} ${s(26)}")
                    property("border-radius", s(22))
                    property("background", "#0061ff")
                    property("opacity", if (groupName.isNotBlank()) "1" else "0.5")
                },
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                if (isCreating) t.chatCreating else t.chatCreateCommunity,
                modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite)
            )
        }
    }
}

// ── Following list item ──

@Composable
private fun FollowingListItem(
    user: FollowUserDto,
    isCreating: Boolean,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .cursor(if (isCreating) Cursor.Default else Cursor.Pointer)
            .onClick { if (!isCreating) onClick() }
            .styleModifier { property("gap", s(8)) },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Same Highlight-sized PlayerCard treatment as the followers list /
        // chat list rows. Falls back to a rounded photo tile when the
        // followee hasn't generated a card yet.
        val cardFrameUrl = user.cardImageUrl
        if (cardFrameUrl != null) {
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("flex-shrink", "0")
                        property("width", s(40))
                        property("height", s(60))
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                    },
                contentAlignment = Alignment.Center
            ) {
                PlayerCard(
                    playerName = "",
                    rating = 0,
                    position = "",
                    sport = "football",
                    size = PlayerCardSize.Highlight,
                    cardFrameUrl = cardFrameUrl,
                    avatarUrl = user.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                    hideRating = true,
                    hideShadow = true,
                )
            }
        } else {
            val avatarUrl = user.avatarUrl
            Box(
                modifier = Modifier
                    .sWidth(40)
                    .sHeight(60)
                    .styleModifier {
                        property("border-radius", s(6))
                        property("background", "rgba(60, 121, 241, 0.15)")
                        property("overflow", "hidden")
                        property("flex-shrink", "0")
                    },
                contentAlignment = Alignment.Center
            ) {
                if (avatarUrl != null) {
                    Image(
                        src = avatarUrl,
                        modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover)
                    )
                } else {
                    SpanText(
                        user.username.take(1).uppercase(),
                        modifier = Modifier
                            .bodyLMedium()
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }
            }
        }
        Column(modifier = Modifier.styleModifier { property("gap", s(2)) }) {
            SpanText(
                user.fullName ?: user.username,
                modifier = Modifier
                    .bodyLRegular()
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .whiteSpace(WhiteSpace.NoWrap)
                    .overflow(Overflow.Hidden)
                    .textOverflow(TextOverflow.Ellipsis)
            )
            SpanText(
                "@${user.username}",
                modifier = Modifier
                    .bodySRegular()
                    .color(com.arena_web.ui.theme.ThemeVars.Text3)
            )
        }
    }
}
